import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { internalUserService } from '../../services/internalUserService';
import { InternalUserRole } from '../../types';
import type { RootState } from '../index';

/**
 * internalUserRoles slice
 *
 * Holds the list of roles assignable to internal users, sourced from
 * GET /Roles?userType=Internal (compliance-user-service — already excludes
 * deprecated roles server-side). Fetched lazily on the Internal User
 * Management page's mount, not at login, since it's only needed there.
 */

export type InternalUserRolesStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

interface InternalUserRolesState {
  roles: InternalUserRole[];
  status: InternalUserRolesStatus;
  error: string | null;
}

const initialState: InternalUserRolesState = {
  roles: [],
  status: 'idle',
  error: null,
};

export const loadAssignableInternalRoles = createAsyncThunk<
  InternalUserRole[],
  void,
  { rejectValue: string }
>('internalUserRoles/loadAssignableInternalRoles', async (_, thunkApi) => {
  try {
    return await internalUserService.getAssignableRoles();
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to load assignable roles';
    return thunkApi.rejectWithValue(message);
  }
});

const internalUserRolesSlice = createSlice({
  name: 'internalUserRoles',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadAssignableInternalRoles.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loadAssignableInternalRoles.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.roles = action.payload;
        state.error = null;
      })
      .addCase(loadAssignableInternalRoles.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ?? action.error.message ?? 'Failed to load assignable roles';
      });
  },
});

export const selectAssignableInternalRoles = (state: RootState): InternalUserRole[] =>
  state.internalUserRoles.roles;

export const selectInternalUserRolesStatus = (state: RootState): InternalUserRolesStatus =>
  state.internalUserRoles.status;

export const selectInternalUserRolesError = (state: RootState): string | null =>
  state.internalUserRoles.error;

export default internalUserRolesSlice.reducer;
